import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Heading } from '@sendible/design-system';
import { useBridgeUser } from '@sendible/shared-state-bridge';
import { EVENTS, publish } from '@sendible/event-manager';
import { useTranslation } from 'react-i18next';
import { useGetHolidays } from '@sendible/frontendv2/src/models/calendar';
import { Container, DayRow, DayLabel, MessageList, EmptyDay, EmptyState } from './index.styles';
import { useCurrentDateAutoScroll, useFirstVisibleDay, usePinUntilLoaded, useProgrammaticScroll, useReleasePinWhenLoaded, useScroll } from './hooks';
import { closestDate } from './segments';
import { useCalendarSegments } from './useCalendarSegments';
import { CalendarCard } from '../../../CalendarCard';
import { ListCalendarCard } from '../../../ListCalendarCard';
import { useCalendarContext } from '../../../../context';
import { useSelectionContext } from '../../../../selectionContext';
import { formatDayLabel, formatISOToLong, fullDateToMonthString, getCalendarCardMedias, groupHolidaysByDate } from '../../../../utils';
import { Holidays } from '../Holidays';

export const DailyGrid = () => {
  const user = useBridgeUser();
  const {
    currentDate,
    filterParams,
    hideEmptyDates,
    openSidebar,
    todayDate,
    userWeekStartDay,
    showHolidays,
    density,
    dailySortOrder,
    setIsFetching,
  } = useCalendarContext();
  const { t } = useTranslation('calendar');
  // What the list is a list of. A change restarts it around the date in view, so the viewport stays on that date.
  const filterKey = useMemo(() => JSON.stringify({ filterParams, density }), [filterParams, density]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const elementContainerRef = containerRef as import('react').RefObject<HTMLElement>;
  const { scrollToDate, programmaticRef } = useProgrammaticScroll(elementContainerRef);

  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);

  const {
    data: holidays,
    isFetching: isFetchingHolidays,
    isLoading: isLoadingHolidays,
  } = useGetHolidays(currentDate, userWeekStartDay, { enabled: showHolidays });

  const holidaysByDate = useMemo(() => groupHolidaysByDate(holidays), [holidays]);

  const isGettingHolidays = showHolidays && isFetchingHolidays && isLoadingHolidays;

  const {
    segments,
    days,
    messagesByDay,
    rowCount,
    isLoading,
    isFetching,
    hasMore,
    extendPastData,
    extendFutureData,
    ensureDate: ensureDateInList,
    reportViewDate,
  } = useCalendarSegments({ initialFocus: currentDate, resetFocus: todayDate, filterKey, hideEmptyDates, isEnabled: true });
  const displayDays = dailySortOrder === 'newest' ? [...days].reverse() : days;
  const { clearVisibleMessageIds, setVisibleMessageIds } = useSelectionContext();

  useEffect(() => {
    setVisibleMessageIds(
      'daily-grid',
      Object.values(messagesByDay)
        .flat()
        .map((message) => message.id)
    );
  }, [messagesByDay, setVisibleMessageIds]);

  useEffect(() => () => clearVisibleMessageIds('daily-grid'), [clearVisibleMessageIds]);

  useScroll(elementContainerRef, {
    programmaticRef,
    sortOrder: dailySortOrder,
    onNearPastBoundary: extendPastData,
    onNearFutureBoundary: extendFutureData,
    // Empty dates are rows too, so the content also changes when only days were added. A page swapped for another
    // leaves the counts alone, so the first and last day are part of it.
    recheckKey: `${rowCount}|${days.length}|${days[0]}|${days[days.length - 1]}`,
  });

  const { firstVisible } = useFirstVisibleDay(elementContainerRef, days, undefined);

  useEffect(() => {
    reportViewDate(firstVisible);
  }, [firstVisible, reportViewDate]);

  const scrollAnchorISO = firstVisible ?? todayDate;
  const lastAnchorTopRef = useRef<number>(0);
  const lastAnchorElRef = useRef<HTMLElement | null>(null);

  // When filters change, or empty dates are shown or hidden: restart Phase A, keeping the date that is in view
  // rather than going back to today.
  const pinState = usePinUntilLoaded(`${filterKey}|${hideEmptyDates}`, firstVisible, currentDate);
  const { initialLoadCompleteRef, pinDateRef, pinTo } = pinState;

  // A jump (Today, month navigation, a date in the sidebar) to a date the list did not hold replaces the list. Keep that
  // date at the top while the new one loads, or what loads above it moves it out of view.
  const ensureDate = useCallback(
    (iso: CalendarMonthString) => {
      const startedAgain = ensureDateInList(iso);

      if (startedAgain) pinTo(iso);

      return startedAgain;
    },
    [ensureDateInList, pinTo]
  );

  useEffect(() => {
    setIsFetching(isFetching);
  }, [isFetching, setIsFetching]);

  // Scroll position management:
  //
  // Phase A — initial load: pin today to the top on every commit until the first pages have arrived.
  //   scrollTop = today.offsetTop  (runs before first paint, no visible jump)
  //
  // Phase B — post-load: compensate when content grows above the anchor (e.g. user scrolled
  //   to an earlier range and new past days loaded). Compare current offsetTop against the
  //   value recorded after the PREVIOUS commit and add the delta to scrollTop. The delta is negative
  //   when a segment far above the viewport has been dropped.
  //
  // Both phases run in a single useLayoutEffect so they always operate on the committed DOM.
  // Recording happens at the END of the effect so the next invocation can compare against it —
  // this is the cross-commit record/compare pattern.
  //
  // Reading offsetTop forces a synchronous layout flush, so the dependency list is everything that
  // can change the rendered rows rather than nothing at all: running on every commit made each
  // unrelated re-render (a context change, a holidays refetch) reflow the whole grid. `displayDays`
  // is deliberately not a dependency — it reallocates on every render in 'newest' order, which would
  // put us straight back to running every time.

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    let anchorEl = container.querySelector<HTMLElement>(`[data-date="${scrollAnchorISO}"]`);

    if (!initialLoadCompleteRef.current && pinDateRef.current) {
      // The pinned date may have no row in the new list, so pin the closest date that has one.
      const rows = Array.from(container.querySelectorAll<HTMLElement>('[data-date]'));
      const closest = closestDate(
        rows.map((row) => row.dataset.date as string),
        pinDateRef.current
      );

      anchorEl = rows.find((row) => row.dataset.date === closest) ?? null;
    }

    if (!anchorEl) return;

    const currentTop = anchorEl.offsetTop;

    if (!initialLoadCompleteRef.current) {
      // Phase A: keep today pinned while data is still loading.
      container.scrollTop = currentTop;
    } else if (anchorEl === lastAnchorElRef.current) {
      // Phase B: compensate for content inserted above the anchor.
      const delta = currentTop - lastAnchorTopRef.current;

      if (delta !== 0) container.scrollTop += delta;
    }

    // Record for the next commit's comparison.
    lastAnchorElRef.current = anchorEl;
    lastAnchorTopRef.current = currentTop;
  }, [days, dailySortOrder, messagesByDay, todayDate, showHolidays, holidaysByDate, isGettingHolidays, density, hideEmptyDates, scrollAnchorISO]);

  // Transition from phase A to phase B once the first pages are loaded. The rest loads on scroll.
  useReleasePinWhenLoaded(pinState, isLoading, segments);

  useCurrentDateAutoScroll({
    firstVisible,
    containerRef: elementContainerRef,
    days,
    ensureDate,
    programmaticRef,
    scrollToDate,
    todayISO: todayDate,
    isSettled: !isLoading && !isFetching,
  });

  const getPrintLabel = () => {
    if (firstVisible == null || days.length === 0) return '';

    const startISO = firstVisible;
    const endISO = days[days.length - 1];
    const startText = formatISOToLong(startISO, user?.language);
    const endText = formatISOToLong(endISO, user?.language);

    return startText && endText ? `${startText} - ${endText}` : '';
  };

  // One label per day, built from the cached Intl formatters, and only rebuilt when the days or the locale change
  // rather than on every render of every row.
  const dayLabels = useMemo(() => {
    const labels: Record<string, ReturnType<typeof formatDayLabel>> = {};

    days.forEach((iso) => {
      labels[iso] = formatDayLabel(iso, user?.language);
    });

    return labels;
  }, [days, user?.language]);

  return (
    <>
      <Heading
        semantic="h2"
        variation="component-title_20"
      >
        {getPrintLabel()}
      </Heading>
      <Container
        ref={containerRef}
        className="daily-grid"
      >
        {displayDays.map((iso) => {
          const { dayNum, month, weekday } = dayLabels[iso] ?? formatDayLabel(iso, user?.language);
          const msgs = messagesByDay[iso] || [];
          const isPastVisible = firstVisible ? iso < firstVisible : false;
          const hideForPrint = msgs.length === 0 || isPastVisible;
          const isPastDay = iso < todayDate;
          const showHoliday = !isPastDay && showHolidays && holidaysByDate && holidaysByDate[iso]?.length > 0;

          // Dates without messages are only rows when they are asked for. The list only holds dates whose
          // messages have been loaded, so a row never appears empty and then fills in.
          if (msgs.length === 0 && hideEmptyDates) return null;

          return (
            <DayRow
              data-testid="calendar-daily-view-row"
              key={iso}
              data-date={iso}
              className={hideForPrint ? 'no-print' : ''}
              data-export-hide={hideForPrint || undefined}
              $mode={density}
              // Lets an off-screen row be sized from how many cards it holds rather than a blanket
              // guess. See contain-intrinsic-height in index.styles.ts.
              $dayRowCards={Math.max(msgs.length, 1)}
            >
              <DayLabel
                data-testid="calendar-daily-day-label"
                $isToday={iso === todayDate}
                onClick={() => !programmaticRef.current && scrollToDate(iso, 'start')}
              >
                <span>
                  <b>{dayNum}</b> {month}, {weekday}
                </span>
              </DayLabel>
              <MessageList>
                {showHoliday && density === 'compact' && (
                  <Holidays
                    isLoading={isGettingHolidays}
                    holidays={holidaysByDate[iso]}
                    onClick={(text: string, image: string) =>
                      publish(EVENTS.OPEN_COMPOSE, { mode: 'new', date: iso, offset: user.timezone, preFillText: text, preFillImage: image })
                    }
                  />
                )}

                {msgs.length === 0 ? (
                  <EmptyDay data-testid="calendar-daily-empty-date">{t('no_posts_that_day')}</EmptyDay>
                ) : (
                  msgs.map((m) =>
                    density === 'compact' ? (
                      <CalendarCard
                        id={m.id}
                        onClick={() => openSidebar('message', fullDateToMonthString(m.sendDateLocalized), m.id)}
                        deliveryStatus={m.deliveryStatus}
                        key={m.id}
                        postAt={m.sendDateLocalized}
                        destinations={m.destinations || []}
                        status={m.status}
                        medias={getCalendarCardMedias(m.body)}
                        recurrence={m.recurrence}
                        title={m.subject || m.body?.messageText}
                        type="daily"
                        workspace={isWorkspaceMode ? m.author || m.workspace : m.workspace}
                      />
                    ) : (
                      <ListCalendarCard
                        id={m.id}
                        key={m.id}
                        campaigns={m.campaigns}
                        deliveryStatus={m.deliveryStatus}
                        postAt={m.sendDateLocalized}
                        destinations={m.destinations || []}
                        status={m.status}
                        medias={getCalendarCardMedias(m.body)}
                        recurrence={m.recurrence}
                        recipients={m.deliveryStatus?.recipients}
                        title={m.body?.messageText || m.subject}
                        workspace={isWorkspaceMode ? m.author || m.workspace : m.workspace}
                      />
                    )
                  )
                )}
              </MessageList>
            </DayRow>
          );
        })}
        {!isLoading && !hasMore && Object.keys(messagesByDay).length === 0 && (
          <EmptyState>
            <Heading
              semantic="h3"
              variation="list-title_12"
            >
              {t('no_posts_found')}
            </Heading>
          </EmptyState>
        )}
      </Container>
    </>
  );
};
